import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Gift, Copy, Check, Users, CalendarPlus } from "lucide-react";

export default function ReferralModal({ code, onClose }) {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/mine").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Codice invito copiato!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
      <div data-testid="referral-modal" className="w-full max-w-sm bg-black border border-amber-500/25 rounded-md gold-glow fade-up">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-amber-500/15">
          <h2 className="font-heading font-bold text-sm text-white uppercase tracking-tight flex items-center gap-2">
            <Gift className="w-4 h-4 text-[#E3BC55]" /> Invita un Amico
          </h2>
          <button data-testid="referral-close-button" onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-300 leading-snug">
            Chi riscatta il tuo codice riceve <span className="text-[#E3BC55] font-semibold">3 giorni gratis</span> e tu guadagni
            <span className="text-emerald-400 font-semibold"> +2 giorni</span> per ognuno.
          </p>

          <div className="flex gap-2">
            <div data-testid="referral-code-display" className="flex-1 bg-black border border-amber-500/30 rounded-sm px-3 py-2.5 font-mono-data text-lg tracking-[0.25em] text-center text-amber-300">
              {code}
            </div>
            <button
              data-testid="referral-copy-button"
              onClick={copyCode}
              className="px-3 rounded-sm bg-[#E3BC55] text-black hover:bg-[#FFD700] transition-colors flex items-center justify-center"
              title="Copia codice"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>

          <div className="flex items-center justify-around bg-black border border-amber-500/15 rounded-md py-2.5">
            <div data-testid="referral-stat-count" className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#E3BC55]" />
              <span className="font-mono-data text-xl font-bold text-amber-300">{stats ? stats.count : "—"}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-widest">invitati</span>
            </div>
            <div className="w-px h-6 bg-slate-700/60" />
            <div data-testid="referral-stat-days" className="flex items-center gap-2">
              <CalendarPlus className="w-4 h-4 text-emerald-400" />
              <span className="font-mono-data text-xl font-bold text-emerald-400">{stats ? `+${stats.days_earned}` : "—"}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-widest">giorni</span>
            </div>
          </div>

          <p className="text-[10px] text-slate-500 leading-snug">
            L'amico inserisce questo codice all'accesso tramite <span className="text-[#E3BC55]">"Ho un invito"</span>.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
